import type { LDContext, LDContextCommon } from "@launchdarkly/node-server-sdk";

// Feature flag keys from LaunchDarkly. Must match configured flags.

export enum Flag {
  OPEN_ENROLLMENT_CHAT_OPTION_ENABLED = "open-enrollment-chat-option",
  CASE_ONBOARDING_AGENT_HUB_PROMPT = "case-onboarding-agent-hub-prompt",
  ENDPOINT_CDP_PROVIDER_OVERRIDE = "endpoint-cdp-provider-override",
  GENERATE_REVIEW_ISSUE_TASKS = "generate-review-issue-tasks",
  GENERATE_REVIEW_FINDING_TASKS = "generate-review-finding-tasks",
  PLAYWRIGHT_CAPTURE_HAR_ARTIFACT = "playwright-capture-har-artifact",
  PLAYWRIGHT_FILL_STRATEGY = "playwright-fill-strategy",
  PLAYWRIGHT_MOVE_CURSOR_STRATEGY = "playwright-move-cursor-strategy",
  BROWSERBASE_PROXY_STRATEGY = "browserbase-proxy-strategy",
  BROWSERBASE_STEALTH_STRATEGY = "browserbase-stealth-strategy",
  BROWSERBASE_CUSTOM_SESSION_SETTINGS = "browserbase-custom-session-settings",
  CONNECTOR_BASED_CDP_INIT = "connector-based-cdp-init",
  RUN_CLASSIC_CRAWL = "run-classic-crawl",
  CLASSIC_CRAWL_REQUESTS_PER_SECOND = "classic-crawl-requests-per-second",
  RUN_AI_CRAWL = "run-ai-crawl",
  COMPARE_FAIR_MARKET_VALUE = "compare-fair-market-value",
  AI_CRAWL_ENABLE_COVERAGE_AND_MEMBER_DISCOVERY = "ai-crawl-enable-coverage-and-member-discovery",
  AI_CRAWL_ENABLE_CLAIMS_LIST_DISCOVERY = "ai-crawl-enable-claims-list-discovery",
  AI_CRAWL_ENABLE_CLAIM_DETAILS_VISITS = "ai-crawl-enable-claim-details-visits",
  AI_CRAWL_ENABLE_PLAN_DOCUMENTS = "ai-crawl-enable-plan-documents",
  AI_CRAWL_ENABLE_MEMBER_ID_CARDS = "ai-crawl-enable-member-id-cards",
  AI_CRAWL_ENABLE_MFA_CONFIGURATION_DISCOVERY = "ai-crawl-enable-mfa-configuration-discovery",
  AI_CRAWL_ENABLE_ACCUMULATORS_DISCOVERY = "ai-crawl-enable-accumulators-discovery",
  AI_CRAWL_ENABLE_PARALLELIZATION = "enable-ai-crawl-parallelization",
  AI_CRAWL_FILE_GRABBER_MODEL = "ai-crawl-file-grabber-model",
  AI_CRAWL_STEPPER_V2_MODEL = "ai-crawl-stepper-v2-model",
  AI_CRAWL_LARGE_MODEL = "ai-crawl-large-model",
  AI_CRAWL_SMALL_MODEL = "ai-crawl-small-model",
  RPA_DEVICE = "rpa-device",
  RPA_TEMPORAL_HEARTBEAT_SCHEDULE_SECONDS = "rpa-temporal-heartbeat-schedule-seconds",
  SIGN_IN_ROBOT_AI_MODEL = "sign-in-robot-ai-model",
  SIGN_IN_LOGIN_SENTINEL_MODEL = "sign-in-login-sentinel-model",
  PARSE_COMPLETER_MODEL = "parse-completer-model",
  SIGN_IN_TRACE_FAILED_HEDGES = "sign-in-trace-failed-hedges",
  SIGN_IN_DIRECTLY = "sign-in-directly",
  SIGN_IN_FUSE_MFA_CODE_SUBMISSION = "sign-in-fuse-mfa-code-submission",
  SIGN_IN_APPLY_LINK_CREATION_ACCESS_POLICIES = "sign-in-apply-link-creation-access-policies",
  SIGN_IN_MULTI_BOX_MFA = "sign-in-multi-box-mfa",
  SIGN_IN_CONCURRENT_VIEWS = "sign-in-concurrent-views",
  SIGN_IN_STRICT_MODE = "sign-in-strict-mode",
  SIGN_IN_AI_PROMPT = "sign-in-ai-prompt",
  SIGN_IN_TIME_OUT_IO_TOOLS = "sign-in-timeout-io-tools",
  SIGN_IN_EXIT_SURVEY = "sign-in-exit-survey",
  SIGN_IN_NEOCLASSICALLY = "sign-in-neoclassically",
  NEO_FALLBACK_AGENT = "neo-fallback-agent",
  NEO_EXTRA_SUPERVISION = "neo-extra-supervision",
  WORKFLOWAI_INVOICE_EXTRACTION_TASK_VERSION = "workflowai-invoice-extraction-task-version",
  AI_INGESTION_PERSIST_TRANSLATED_RESOURCES = "ai-ingestion-persist-translated-resources",
  INGESTION_DATA_QUALITY_ENABLED = "ingestion-data-quality-enabled",
  VELLUM_URL_ANALYSIS_RELEASE_TAG = "vellum-url-analysis-release-tag",
  VELLUM_CLASSIFY_CRAWLER_ARTIFACTS_RELEASE_TAG = "vellum-classify-crawler-artifacts-release-tag",
  VELLUM_CLASSIFY_DOCUMENT_RELEASE_TAG = "vellum-classify-document-release-tag",
  VELLUM_RESOURCE_ANALYZER_RELEASE_TAG = "vellum-resource-analyzer-release-tag",
  VELLUM_RESOURCE_RANKER_RELEASE_TAG = "vellum-resource-ranker-release-tag",
  ONBOARDING_TIME_LIMIT = "onboarding-time-limit",
  AI_CRAWL_ENABLE_CLAIM_PDF_VISITS = "ai-crawl-enable-claim-pdf-visits",
  EOB_FINDINGS_V1_ENABLED = "eob-findings-v1",
  AUTO_ANALYZE_SIGN_IN_LOGS = "auto-analyze-sign-in-logs",
  // TODO: remove these flags
  CASE_INTAKE_V1 = "case-intake-v-1",
  CASE_INTAKE_V1_PHASE_2 = "case-intake-v-1-phase-2",
  SHOW_PAYMENT_IN_SETTINGS = "show-payment-in-settings",
  AI_CRAWL_SPECIALIST_INSTRUCTIONS_REF = "ai-crawl-specialist-instructions-ref",
  CASE_AUTOMATION_ENABLED = "case-automation-enabled",
  USE_GENERIC_AGENT_FOR_ONBOARDING = "use-generic-agent-for-onboarding",
  MANAGED_MFA_ENABLED = "managed-mfa-enabled",
  HOME_SCREEN_ENABLE_GETTING_STARTED_MODULE = "home-screen-enable-getting-started-module",
  HOME_SCREEN_SHOW_FLOATING_CREATE_CASE_BUTTON = "home-screen-show-floating-create-case-button",
  BOT_CONTROL_ENABLED = "bot-control-enabled",
  BENEFITS_RAG_ENABLED = "benefits-q-a-enabled",
  VELLUM_BENEFIT_SUMMARY_RELEASE_TAG = "vellum-benefit-summary-release-tag",
  BENEFIT_SUMMARY_WORKFLOW_ENABLED = "benefit-summary-workflow-enabled",
  HOME_SCREEN_SHOW_GETTING_STARTED_V2 = "home-screen-getting-started-v-2",
  USE_FRONTEND_SELECTION_EVENTS = "use-frontend-selection-events",
  OAUTH_ENABLED = "o-auth-enabled",
  VONAGE_SMS_CC_EMAILS = "vonage-sms-cc-emails",
  SHOW_FEEDBACK_BUTTONS = "show-feedback-buttons",
  SHOW_FEEDBACK_ACTION_BUTTONS = "show-feedback-action-buttons",
  INSURANCE_LOGIN_HELP_VIEW_GUIDES = "insurance-login-help-view-guides",
  SHOW_LOGIN_READY_SCREEN = "show-login-ready-screen",
  USE_ADAPTIVE_DIALOG_SHEET_YSTACK = "use-adaptive-dialog-sheet-ystack",
  SHOW_PLAN_DOCUMENTS_LOADING = "show-plan-documents-loading",
  PLAN_DOCUMENTS_LOADING_UI_TIMEOUT_SECONDS = "plan-documents-loading-ui-timeout-seconds",
  UNIFIED_CASE_CREATION_FLOW = "unified-case-creation-flow",
  INSURANCE_EXIT_SURVEY_CUSTOM_TEXT = "insurance-exit-survey-custom-text",
  INSURANCE_LINKING_PROGRESS_TEXTS = "insurance-linking-progress-texts",
  SHOW_P3_ENDPOINTS = "show-p3-endpoints",
  FORCE_SHOW_WELCOME_SCREEN = "force-show-welcome-screen",
  CASE_AUTOMATION_V2_VERCEL = "case-automation-v2-vercel",
  INITIAL_DOCUMENT_ANALYSIS_ENABLED = "initial-document-analysis-enabled",
  NEW_DOCUMENT_UPLOAD_UI = "new-document-upload-ui",
  CASE_AUTOMATION_BILL_CHECKER = "case-automation-bill-checker",
  CASE_AUTOMATION_ROUTER_WORKFLOW = "case-automation-router-workflow",
  CASE_AUTOMATION_HIPAA_RELEASE_TOOL = "case-automation-hipaa-release-tool",
  CASE_AUTOMATION_SPECIAL_MESSAGING = "case-automation-special-messaging",
  NEW_BILL_UPLOAD_UI = "new-bill-upload-ui",
  SHOW_POST_ONBOARDING_REFERRAL_MODAL = "show-post-onboarding-referral-modal",
  SHOW_EHR_DEFAULT_LINKS = "show-ehr-default-links",
  NEW_WEB_NAVIGATION = "new-web-navigation",
  SHOW_PROMO_CODE_INPUT = "show-promo-code-input",
}

export type Contexts =
  | "endpointSlug"
  | "user"
  | "toPhoneNumber"
  | "executionId";

/** Acceptable contexts for LaunchDarkly. */
export type KnownLDContext = { kind: "multi" } & {
  // This enforces that at least one of the Context properties is present.
  // Otherwise, LD's client will error at runtime.
  [K in Contexts]: { [P in K]: LDContextCommon } & {
    [P in Exclude<Contexts, K>]?: LDContextCommon;
  };
}[Contexts];

({}) as KnownLDContext satisfies LDContext;

export const EMPTY_LD_CONTEXT: KnownLDContext = {
  kind: "multi",
  user: { key: "empty" },
};
